import {
  Dribbble,
  FacebookRound,
  FormerlyKnownAsTwitter,
  GitHub,
  Instagram,
  LinkedIn,
} from '@/client/components/icons/ContinuIcons';
import {
  FormControl,
  Grid,
  GridItem,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { defaultSocialLinks, validateSocialLink } from '@/client/utils/socialLinks';

import type { SocialLink } from '@/client/types/User';
import type { UserDetailsFormData } from '@/client/components/learners/profile/overlay/LearnerProfileUserDetailsFormModal';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// Map icons to social link names
const socialLinkIcons = {
  linkedin: LinkedIn,
  twitter: FormerlyKnownAsTwitter,
  facebook: FacebookRound,
  instagram: Instagram,
  dribbble: Dribbble,
  github: GitHub,
};

export default function LearnerProfileSocialLinksForm() {
  const { t } = useTranslation();
  const { watch, setValue, getValues } = useFormContext<UserDetailsFormData>();

  const { socialLinks } = watch();

  // Helper function to get current value for a social link
  const getCurrentValue = (socialLinkName: string): string => {
    const existingLink = socialLinks?.find((link) => link.name === socialLinkName);
    return existingLink?.value || '';
  };

  // Helper function to update a social link
  const updateSocialLink = (socialLinkName: string, value: string) => {
    const currentSocialLinks = getValues('socialLinks') || [];
    const existingIndex = currentSocialLinks.findIndex((link) => link.name === socialLinkName);

    if (value.trim() === '') {
      // Remove the link if value is empty
      if (existingIndex !== -1) {
        const updatedLinks = currentSocialLinks.filter((_, index) => index !== existingIndex);
        setValue('socialLinks', updatedLinks, { shouldDirty: true, shouldValidate: true });
      }
    } else {
      // Update or add the link
      const newLink: SocialLink = {
        name: socialLinkName,
        value: value.trim(),
        _id:
          existingIndex !== -1 ? currentSocialLinks[existingIndex]._id : `temp_${socialLinkName}`,
      };

      if (existingIndex !== -1) {
        const updatedLinks = [...currentSocialLinks];
        updatedLinks[existingIndex] = newLink;
        setValue('socialLinks', updatedLinks, { shouldDirty: true, shouldValidate: true });
      } else {
        setValue('socialLinks', [...currentSocialLinks, newLink], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };

  return (
    <Stack spacing={6}>
      <Text variant="formSectionHeading">{t('learners.profile.socialLinks.title')}</Text>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
        {defaultSocialLinks.map((socialLink) => {
          const currentValue = getCurrentValue(socialLink.name);
          const validationError = validateSocialLink(socialLink.name, currentValue);

          return (
            <GridItem key={socialLink.name}>
              <FormControl isInvalid={!!validationError}>
                <Stack spacing={2}>
                  <Text variant="formlabel">{socialLink.title}</Text>

                  <Stack spacing={0}>
                    <InputGroup>
                      <InputLeftElement>
                        <Icon
                          as={socialLinkIcons[socialLink.name as keyof typeof socialLinkIcons]}
                          color="warmNeutral.300"
                        />
                      </InputLeftElement>

                      <Input
                        placeholder={socialLink.placeholder}
                        variant="warmNeutral"
                        _placeholder={{ fontWeight: 500, color: 'neutral.600' }}
                        value={currentValue}
                        onChange={(e) => updateSocialLink(socialLink.name, e.target.value)}
                      />
                    </InputGroup>

                    {validationError && <Text variant="formErrorText">{validationError}</Text>}
                  </Stack>
                </Stack>
              </FormControl>
            </GridItem>
          );
        })}
      </Grid>
    </Stack>
  );
}
